import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, X, ChevronDown, Search, Users, Plus, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';
import auditRiskService from '../../../../../../services/audit_services/auditRiskService';

export default function AuditPlanCreate() {
  const [searchParams] = useSearchParams();
  const programId = searchParams.get('program'); // Get program ID from query param
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    objectives: '',
    scope: '',
    reference: '',
    program_id: programId || '',
    program_title: '', // Store program title for display
    parties: [], // Users from audit program with their roles
    risks: [], // Selected risks with probability
    location: '', // Separate location field
    note: '' // Separate note field
  });

  // Available programs
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  // Program search
  const [programSearch, setProgramSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Available program parties (users from selected audit program)
  const [programParties, setProgramParties] = useState([]);
  const [loadingProgramParties, setLoadingProgramParties] = useState(false);

  // User search for parties from program
  const [partySearch, setPartySearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Risk search
  const [riskSearch, setRiskSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Attachments
  const [attachments, setAttachments] = useState([]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (formData.program_id) {
      fetchProgramParties(formData.program_id);
    }
  }, [formData.program_id]);

  const fetchPrograms = async () => {
    try {
      const result = await auditProgramService.searchAuditPrograms(authTokens.access, '');
      
      if (result.success) {
        setPrograms(result.data);
        // If programId from URL, find and set the program title
        if (programId) {
          const selectedProgram = result.data.find(p => p.id === parseInt(programId));
          if (selectedProgram) {
            setFormData(prev => ({ ...prev, program_title: selectedProgram.title }));
          }
        }
      } else {
        console.error('Error fetching programs:', result.error);
        showError('Error loading programs');
      }
      setLoadingPrograms(false);
    } catch (error) {
      console.error('Error fetching programs:', error);
      showError('Error loading programs');
      setLoadingPrograms(false);
    }
  };

  const fetchProgramParties = async (programId) => {
    setLoadingProgramParties(true);
    try {
      const result = await auditProgramService.getProgramParties(authTokens.access, programId);
      
      if (result.success) {
        setProgramParties(result.data);
      } else {
        console.error('Error fetching program parties:', result.error);
        showError('Error loading program parties');
      }
      setLoadingProgramParties(false);
    } catch (error) {
      console.error('Error fetching program parties:', error);
      showError('Error loading program parties');
      setLoadingProgramParties(false);
    }
  };

  // Program search functionality
  const handleProgramSearch = async (query) => {
    setProgramSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const result = await auditProgramService.searchAuditPrograms(authTokens.access, query);
        
        if (result.success) {
          setProgramSearch(prev => ({
            ...prev,
            results: result.data,
            showDropdown: true,
            loading: false
          }));
        } else {
          console.error('Error searching programs:', result.error);
          setProgramSearch(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error searching programs:', error);
        setProgramSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setProgramSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const selectProgram = (program) => {
    setFormData(prev => ({
      ...prev,
      program_id: program.id,
      program_title: program.title,
      parties: [] // Clear parties when program changes
    }));
    
    // Clear search
    setProgramSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  // Party search functionality (from program parties)
  const handlePartySearch = async (query) => {
    setPartySearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const filteredParties = programParties.filter(party =>
          party.name.toLowerCase().includes(query.toLowerCase()) ||
          party.email.toLowerCase().includes(query.toLowerCase()) ||
          party.partyRole.toLowerCase().includes(query.toLowerCase())
        );

        setPartySearch(prev => ({
          ...prev,
          results: filteredParties,
          showDropdown: true,
          loading: false
        }));
      } catch (error) {
        console.error('Error searching parties:', error);
        setPartySearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setPartySearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  // Risk search functionality
  const handleRiskSearch = async (query) => {
    setRiskSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const result = await auditRiskService.searchAuditRisks(authTokens.access, query);
        
        if (result.success) {
          setRiskSearch(prev => ({
            ...prev,
            results: result.data,
            showDropdown: true,
            loading: false
          }));
        } else {
          console.error('Error searching risks:', result.error);
          setRiskSearch(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error searching risks:', error);
        setRiskSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setRiskSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const addParty = (party) => {
    // Check if party is already added
    if (!formData.parties.find(p => p.id === party.id)) {
      setFormData(prev => ({
        ...prev,
        parties: [...prev.parties, party]
      }));
    }
    
    // Clear search
    setPartySearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeParty = (partyId) => {
    setFormData(prev => ({
      ...prev,
      parties: prev.parties.filter(p => p.id !== partyId)
    }));
  };

  const addRisk = (risk) => {
    // Check if risk is already added
    if (!formData.risks.find(r => r.id === risk.id)) {
      setFormData(prev => ({
        ...prev,
        risks: [...prev.risks, {
          ...risk,
          probability: 'medium' // Default probability in English for backend
        }]
      }));
    }
    
    // Clear search
    setRiskSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeRisk = (riskId) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.filter(r => r.id !== riskId)
    }));
  };

  const updateRiskProbability = (riskId, probability) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.map(r => 
        r.id === riskId ? { ...r, probability } : r
      )
    }));
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auditor' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditee' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approver' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (programSearch.showDropdown && !event.target.closest('.program-search-container')) {
        setProgramSearch(prev => ({ ...prev, showDropdown: false }));
      }
      if (partySearch.showDropdown && !event.target.closest('.party-search-container')) {
        setPartySearch(prev => ({ ...prev, showDropdown: false }));
      }
      if (riskSearch.showDropdown && !event.target.closest('.risk-search-container')) {
        setRiskSearch(prev => ({ ...prev, showDropdown: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [programSearch.showDropdown, partySearch.showDropdown, riskSearch.showDropdown]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.objectives.trim()) {
      newErrors.objectives = 'Objectives are required';
    }

    if (!formData.scope.trim()) {
      newErrors.scope = 'Scope is required';
    }

    if (!formData.reference.trim()) {
      newErrors.reference = 'Reference standards are required';
    }

    if (!formData.program_id) {
      newErrors.program_id = 'Program selection is required';
    }

    if (formData.parties.length === 0) {
      newErrors.parties = 'At least one party is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    console.log(formData)
    try {
      const result = await auditPlanService.createAuditPlan(formData, authTokens.access);
      
      if (result.success) {
        // Upload attachments if any
        if (attachments.length > 0) {
          for (const file of attachments) {
            await auditPlanService.uploadAttachment(
              authTokens.access,
              result.data.id,
              file,
              ''
            );
          }
        }
        
        showSuccess('Audit plan created successfully');
        navigate('/dashboard/audit/plans');
      } else {
        console.error('Error creating audit plan:', result.error);
        showError(result.error || 'Error creating audit plan');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error creating audit plan:', error);
      showError('Error creating audit plan');
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/dashboard/audit/plans"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Créer un plan d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Créer un nouveau plan d'audit suivant les normes ISO 19011</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations de base</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de l'audit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Audit SI de l'année 2025"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Program */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Programme d'audit <span className="text-red-500">*</span>
              </label>
              <div className="program-search-container relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.program_title || programSearch.query}
                    onChange={(e) => {
                      if (!formData.program_id) {
                        handleProgramSearch(e.target.value);
                      }
                    }}
                    onFocus={() => {
                      if (!formData.program_id) {
                        handleProgramSearch(programSearch.query || '');
                      }
                    }}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.program_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Rechercher et sélectionner un programme..."
                    readOnly={!!formData.program_id}
                  />
                  {formData.program_id ? (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, program_id: '', program_title: '', parties: [] }));
                        setProgramSearch({ query: '', results: [], showDropdown: false, loading: false });
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : (
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  )}
                </div>

                {/* Program Search Dropdown */}
                {programSearch.showDropdown && !formData.program_id && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {programSearch.loading ? (
                      <div className="p-3 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                        <span className="ml-2">Recherche...</span>
                      </div>
                    ) : programSearch.results.length > 0 ? (
                      programSearch.results.map((program) => (
                        <button
                          key={program.id}
                          type="button"
                          onClick={() => selectProgram(program)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">{program.title}</p>
                            <p className="text-xs text-gray-500">
                              {program.objective ? program.objective.substring(0, 100) + '...' : 'Aucune description'}
                            </p>
                          </div>
                        </button>
                      ))
                    ) : programSearch.query.length >= 2 ? (
                      <div className="p-3 text-center text-gray-500">Aucun programme trouvé</div>
                    ) : (
                      <div className="p-3 text-center text-gray-500">Tapez pour rechercher des programmes...</div>
                    )}
                  </div>
                )}
              </div>
              {errors.program_id && <p className="text-red-500 text-sm mt-1">{errors.program_id}</p>}
            </div>
          </div>

          {/* Objectives */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objectifs <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.objectives}
              onChange={(e) => handleInputChange('objectives', e.target.value)}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.objectives ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe the objectives of this audit..."
            />
            {errors.objectives && <p className="text-red-500 text-sm mt-1">{errors.objectives}</p>}
          </div>

          {/* Scope */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Périmètre <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.scope}
              onChange={(e) => handleInputChange('scope', e.target.value)}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.scope ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe the scope of this audit..."
            />
            {errors.scope && <p className="text-red-500 text-sm mt-1">{errors.scope}</p>}
          </div>

          {/* Reference Standards */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Normes de référence <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.reference ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., ISO 27001, ISO 19011"
            />
            {errors.reference && <p className="text-red-500 text-sm mt-1">{errors.reference}</p>}
          </div>
        </div>

        {/* Parties Involved */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Parties impliquées</h3>
          
          {/* Program Selection Notice */}
          {!formData.program_id ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                Veuillez d'abord sélectionner un programme d'audit pour charger les parties disponibles.
              </p>
            </div>
          ) : loadingProgramParties ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement des parties du programme...</p>
            </div>
          ) : (
            <>
              {/* Party Search */}
              <div className="party-search-container relative mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajouter des parties du programme
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={partySearch.query}
                    onChange={(e) => handlePartySearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Rechercher des parties par nom, email ou rôle..."
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>

                {/* Search Dropdown */}
                {partySearch.showDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {partySearch.loading ? (
                      <div className="p-3 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                        <span className="ml-2">Recherche...</span>
                      </div>
                    ) : partySearch.results.length > 0 ? (
                      partySearch.results.map((party) => (
                        <button
                          key={party.id}
                          type="button"
                          onClick={() => addParty(party)}
                          disabled={formData.parties.find(p => p.id === party.id)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                            formData.parties.find(p => p.id === party.id) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {party.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{party.name}</p>
                                <p className="text-xs text-gray-500">{party.email}</p>
                              </div>
                            </div>
                            {getRoleBadge(party.partyRole)}
                          </div>
                        </button>
                      ))
                    ) : partySearch.query.length >= 2 ? (
                      <div className="p-3 text-center text-gray-500">Aucune partie trouvée</div>
                    ) : (
                      <div className="p-3 text-center text-gray-500">Tapez pour rechercher des parties...</div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Parties */}
              {formData.parties.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Parties sélectionnées ({formData.parties.length})</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {formData.parties.map((party) => (
                      <div key={party.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {party.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{party.name}</p>
                            <p className="text-xs text-gray-500">{party.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getRoleBadge(party.partyRole)}
                          <button
                            type="button"
                            onClick={() => removeParty(party.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {errors.parties && <p className="text-red-500 text-sm mt-1">{errors.parties}</p>}
        </div>

        {/* Risks */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
              Risques
            </h3>
          </div>

          {/* Risk Search */}
          <div className="risk-search-container relative mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter des risques de la bibliothèque
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={riskSearch.query}
                onChange={(e) => handleRiskSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Rechercher des risques existants..."
              />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Risk Search Dropdown */}
            {riskSearch.showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {riskSearch.loading ? (
                  <div className="p-3 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                    <span className="ml-2">Recherche...</span>
                  </div>
                ) : riskSearch.results.length > 0 ? (
                  riskSearch.results.map((risk) => (
                    <button
                      key={risk.id}
                      type="button"
                      onClick={() => addRisk(risk)}
                      disabled={formData.risks.find(r => r.id === risk.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        formData.risks.find(r => r.id === risk.id) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{risk.risk_name}</p>
                        <p className="text-xs text-gray-500">{risk.description}</p>
                      </div>
                    </button>
                  ))
                ) : riskSearch.query.length >= 2 ? (
                  <div className="p-3 text-center text-gray-500">Aucun risque trouvé</div>
                ) : (
                  <div className="p-3 text-center text-gray-500">Tapez pour rechercher des risques...</div>
                )}
              </div>
            )}
          </div>

          {/* Selected Risks */}
          {formData.risks.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Risques sélectionnés ({formData.risks.length})</p>
              <div className="space-y-3">
                {formData.risks.map((risk) => (
                  <div key={risk.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{risk.risk_name}</p>
                        <p className="text-xs text-gray-500 mt-1">{risk.description}</p>
                      </div>
                      <div className="flex items-center space-x-3 ml-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
                          <select
                            value={risk.probability}
                            onChange={(e) => updateRiskProbability(risk.id, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="low">Faible</option>
                            <option value="medium">Moyen</option>
                            <option value="high">Élevé</option>
                            <option value="critical">Critique</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRisk(risk.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Location and Note */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Localisation et notes</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: Cameroun, France"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Notes supplémentaires sur l'audit..."
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pièces jointes (optionnel)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
              {attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to="/dashboard/audit/plans"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Création...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Créer le plan d'audit
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}